/* Copyright (c) 2020-present CloudIO Inc */

import { ComponentSample, Item } from '../../registry/Component.type';

export const page: {
  root: Item<'Page'>;
  flexPanel: Item<'FlexPanel'>;
  flexItem: Item<'FlexItem'>;
  boxContainer: Item<'BoxContainer'>;
  box1: Item<'Box'>;
  box2: Item<'Box'>;
  box3: Item<'Box'>;
  typography: Item<'Typography'>;
  typography1: Item<'Typography'>;
  typography2: Item<'Typography'>;
  emptySpacer: Item<'EmptySpacer'>;
} = {
  emptySpacer: {
    children: [],
    itemId: 'emptySpacer',
    parent: 'box1',
    props: {},
    type: 'EmptySpacer',
  },
  boxContainer: {
    children: ['box1', 'box2', 'box3'],
    itemId: 'boxContainer',
    parent: 'flexItem',
    props: {
      bgcolor: {
        type: 'text',
        value: 'background.paper',
      },
      border: {
        all: {
          style: 'solid',
          width: 1,
        },
      },
      borderRadius: '8px',
      boxShadow: '3',
      margin: {
        all: 20,
      },
      padding: {
        all: 4,
      },
    },
    type: 'BoxContainer',
  },
  box1: {
    children: ['typography'],
    itemId: 'box1',
    parent: 'boxContainer',
    props: {
      bgcolor: {
        type: 'text',
        value: 'background.default',
      },
      border: {
        all: {
          style: 'solid',
          width: 1,
        },
      },
      boxHeight: '50px',
      boxWidth: '50px',
      margin: {
        all: 4,
      },
    },
    type: 'Box',
  },
  box2: {
    children: ['typography1'],
    itemId: 'box2',
    parent: 'boxContainer',
    props: {
      bgcolor: {
        type: 'text',
        value: 'background.default',
      },
      border: {
        all: {
          style: 'solid',
          width: 1,
        },
      },
      boxHeight: '50px',
      boxWidth: '50px',
      margin: {
        all: 4,
      },
    },
    type: 'Box',
  },
  box3: {
    children: ['typography2'],
    itemId: 'box3',
    parent: 'boxContainer',
    props: {
      bgcolor: {
        type: 'text',
        value: 'background.default',
      },
      border: {
        all: {
          style: 'solid',
          width: 1,
        },
      },
      boxHeight: '50px',
      boxWidth: '50px',
      margin: {
        all: 4,
      },
    },
    type: 'Box',
  },
  flexItem: {
    children: ['boxContainer'],
    itemId: 'flexItem',
    parent: 'flexPanel',
    props: {},
    type: 'FlexItem',
  },
  flexPanel: {
    children: ['flexItem'],
    itemId: 'flexPanel',
    parent: 'root',
    props: {},
    type: 'FlexPanel',
  },
  root: {
    children: ['flexPanel'],
    itemId: 'root',
    parent: 'root',
    props: {
      windowTitle: {
        type: 'text',
        value: 'Blank Page',
      },
      padding: { all: 0 },
    },
    type: 'Page',
  },
  typography: {
    children: [],
    itemId: 'typography',
    parent: 'box1',
    props: {
      text: {
        type: 'text',
        value: 'A',
      },
    },
    type: 'Typography',
  },
  typography1: {
    children: [],
    itemId: 'typography1',
    parent: 'box2',
    props: {
      text: {
        type: 'text',
        value: 'B',
      },
    },
    type: 'Typography',
  },
  typography2: {
    children: [],
    itemId: 'typography2',
    parent: 'box3',
    props: {
      text: {
        type: 'text',
        value: 'C',
      },
    },
    type: 'Typography',
  },
};

export const helpRenders: ComponentSample<'BoxContainer'>[] = [
  {
    title: 'Basic BoxContainer',
    description:
      'A container component for grouping and organizing multiple Box components with consistent styling',
    page,
    exampleId: 'eg-basic',
    clearParentChildren: true,
    height: 280,
    visibleProps: [
      'bgcolor',
      'borderRadius',
      'boxShadow',
      'margin',
      'padding',
      'border',
    ],
    item: {
      itemId: 'boxContainer',
      type: 'BoxContainer',
      children: ['box1', 'box2', 'box3'],
      parent: 'flexItem',
      props: {
        bgcolor: {
          type: 'text',
          value: 'background.paper',
        },
        borderRadius: '8px',
        boxShadow: '3',
        margin: {
          all: 20,
        },
        padding: {
          all: 4,
        },
        border: {
          all: {
            width: 1,
            style: 'solid',
          },
        },
      },
    },
  },
  {
    title: 'Flex Direction',
    description: `Use Flex Direction to change the direction of the child Box components within the container.

Refer: [https://developer.mozilla.org/en-US/docs/Glossary/Main_Axis](https://developer.mozilla.org/en-US/docs/Glossary/Main_Axis)`,
    page,
    exampleId: 'eg-flexDirection',
    clearParentChildren: true,
    height: 334,
    visibleProps: ['flexDirection'],
    item: {
      itemId: 'boxContainer',
      type: 'BoxContainer',
      children: ['box1', 'box2', 'box3'],
      parent: 'flexItem',
      props: {
        flexDirection: 'column',
        bgcolor: {
          type: 'text',
          value: 'background.paper',
        },
        border: {
          all: {
            width: 1,
            style: 'solid',
          },
        },
        padding: {
          all: 4,
        },
        boxHeight: '334px',
      },
    },
  },
  {
    title: 'Align Items',
    description: `Use Align Items to control the alignment of child Box components within the container.

Refer: [https://developer.mozilla.org/en-US/docs/Web/CSS/align-items](https://developer.mozilla.org/en-US/docs/Web/CSS/align-items)`,
    page,
    exampleId: 'eg-alignItems',
    clearParentChildren: true,
    height: 334,
    visibleProps: ['flexDirection', 'alignItems'],
    item: {
      itemId: 'boxContainer',
      type: 'BoxContainer',
      children: ['box1', 'box2', 'box3'],
      parent: 'flexItem',
      props: {
        flexDirection: 'column',
        alignItems: 'flex-end',
        bgcolor: {
          type: 'text',
          value: 'background.paper',
        },
        border: {
          all: {
            width: 1,
            style: 'solid',
          },
        },
        padding: {
          all: 4,
        },
        boxHeight: '334px',
      },
    },
  },
  {
    title: 'Justify Content',
    description: `Use Justify Content to distribute space between and around child Box components along the main axis.

Refer: [https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content)`,
    page,
    exampleId: 'eg-justifyContent',
    clearParentChildren: true,
    height: 334,
    visibleProps: ['flexDirection', 'justifyContent'],
    item: {
      itemId: 'boxContainer',
      type: 'BoxContainer',
      children: ['box1', 'box2', 'box3'],
      parent: 'flexItem',
      props: {
        flexDirection: 'column',
        justifyContent: 'space-between',
        bgcolor: {
          type: 'text',
          value: 'background.paper',
        },
        border: {
          all: {
            width: 1,
            style: 'solid',
          },
        },
        padding: {
          all: 4,
        },
        boxHeight: '334px',
      },
    },
  },
  {
    title: 'Container with Empty Child',
    description:
      'BoxContainer can hold an EmptySpacer for layout spacing purposes',
    page,
    exampleId: 'eg-emptySpacer',
    clearParentChildren: true,
    height: 280,
    visibleProps: ['bgcolor', 'padding', 'margin'],
    item: {
      itemId: 'boxContainer',
      type: 'BoxContainer',
      children: ['emptySpacer'],
      parent: 'flexItem',
      props: {
        bgcolor: {
          type: 'text',
          value: 'background.paper',
        },
        margin: {
          all: 20,
        },
        padding: {
          all: 40,
        },
        border: {
          all: {
            width: 1,
            style: 'solid',
          },
        },
      },
    },
  },
];
